import React, { useMemo, useState, useCallback } from 'react';
import { StylishNewSearchBar } from '../../../DesignSystems/New/StylishNewSearchBar';
import { StylishNewButton } from '../../../DesignSystems/New/StylishNewButton';
import { ActiveFilterChips } from '../LayersTab/ActiveFilterChips';
import {
  AORHierarchyNode,
  AREA,
  DISTRICT,
  SECTOR,
  SUBUNIT,
} from './utils/schema';
import { useGetAorsByHierarchy } from '../../../../hooks/useGetAorsByHierarchy';
import { HierarchyItemCard } from './HierarchyItemCard';
import { useUscgMapContext } from '../LayersTab/UscgMapContext';
import { SharedIcon } from '../../../SharedIcon/SharedIcon';

const AORs = () => {
  const [searchText, setSearchText] = useState('');
  const [selectedAreaId, setSelectedAreaId] = useState<string | null>(null);
  const [focusedDistrictId, setFocusedDistrictId] = useState<string | null>(
    null
  );
  const [selectedSectorId, setSelectedSectorId] = useState<string | null>(null);
  const { aorFilterIds, setAorFilterIds, isAorVisible, toggleAorVisibility } =
    useUscgMapContext();

  const { data: aorsByHierarchy = [], isLoading } = useGetAorsByHierarchy();

  const allAorIds = useMemo(
    () => aorsByHierarchy.flatMap((level) => level.aors).map((aor) => aor.id),
    [aorsByHierarchy]
  );

  const isAllVisible = useMemo(
    () => isAorVisible && aorFilterIds === null,
    [aorFilterIds, isAorVisible]
  );

  const handleToggleAll = useCallback(() => {
    if (isAllVisible) {
      setAorFilterIds([]);
      toggleAorVisibility();
      return;
    }

    if (!isAorVisible) {
      toggleAorVisibility();
    }

    setAorFilterIds(null);
  }, [isAllVisible, isAorVisible, setAorFilterIds, toggleAorVisibility]);

  const areas = useMemo(
    () => aorsByHierarchy.find((level) => level.levelName === AREA)?.aors ?? [],
    [aorsByHierarchy]
  );

  const districts = useMemo(
    () =>
      aorsByHierarchy.find((level) => level.levelName === DISTRICT)?.aors ?? [],
    [aorsByHierarchy]
  );

  const filteredDistricts = useMemo(() => {
    if (focusedDistrictId) {
      return districts.filter((district) => district.id === focusedDistrictId);
    }

    let result = districts;

    if (selectedAreaId) {
      result = result.filter(
        (district) => district.parentItemId === selectedAreaId
      );
    }

    const normalizedSearch = searchText.trim().toLowerCase();
    if (normalizedSearch.length) {
      result = result.filter((district) =>
        district.name.toLowerCase().includes(normalizedSearch)
      );
    }

    return result;
  }, [districts, focusedDistrictId, selectedAreaId, searchText]);

  const sectorsByDistrictId = useMemo(() => {
    const sectors =
      aorsByHierarchy.find((level) => level.levelName === SECTOR)?.aors ?? [];

    return sectors.reduce<Record<string, AORHierarchyNode[]>>((acc, sector) => {
      if (!sector.parentItemId) {
        return acc;
      }

      if (!acc[sector.parentItemId]) {
        acc[sector.parentItemId] = [];
      }

      acc[sector.parentItemId].push(sector);
      return acc;
    }, {});
  }, [aorsByHierarchy]);

  const subUnitsBySectorId = useMemo(() => {
    const subUnits =
      aorsByHierarchy.find((level) => level.levelName === SUBUNIT)?.aors ?? [];

    return subUnits.reduce<Record<string, AORHierarchyNode[]>>(
      (acc, subUnit) => {
        if (!subUnit.parentItemId) {
          return acc;
        }

        if (!acc[subUnit.parentItemId]) {
          acc[subUnit.parentItemId] = [];
        }

        acc[subUnit.parentItemId].push(subUnit);
        return acc;
      },
      {}
    );
  }, [aorsByHierarchy]);

  const sectorsById = useMemo(() => {
    return Object.values(sectorsByDistrictId)
      .flat()
      .reduce<Record<string, AORHierarchyNode>>((acc, sector) => {
        acc[sector.id] = sector;
        return acc;
      }, {});
  }, [sectorsByDistrictId]);

  const districtsById = useMemo(() => {
    return districts.reduce<Record<string, AORHierarchyNode>>(
      (acc, district) => {
        acc[district.id] = district;
        return acc;
      },
      {}
    );
  }, [districts]);

  const activeDistrict = focusedDistrictId
    ? (districtsById[focusedDistrictId] ?? null)
    : null;
  const activeSector = selectedSectorId
    ? (sectorsById[selectedSectorId] ?? null)
    : null;

  const activeChips = useMemo(() => {
    if (!activeDistrict) {
      return [];
    }

    const chips = [
      {
        filterName: 'District',
        filterValue: activeDistrict.name,
        onRemove: () => {
          setSelectedSectorId(null);
          setFocusedDistrictId(null);
        },
      },
    ];

    if (selectedSectorId && activeSector) {
      chips.push({
        filterName: 'Sector',
        filterValue: activeSector.name,
        onRemove: () => {
          setSelectedSectorId(null);
        },
      });
    }

    return chips;
  }, [activeDistrict, activeSector, selectedSectorId]);

  return (
    <div className="p-2 d-flex flex-column gap-2" style={{ height: '100%' }}>
      <div
        className="position-sticky top-0 pb-2"
        style={{
          borderBottom: '1px solid #2b2f38',
        }}
      >
        <div className="d-flex gap-2 mb-2">
          <StylishNewSearchBar
            value={searchText}
            onChangeSearchTerm={setSearchText}
          />
          <StylishNewButton
            className="button--icon"
            title={
              isAllVisible ? 'Hide all AORs from map' : 'Show all AORs on map'
            }
            aria-label={isAllVisible ? 'Hide all AORs' : 'Show all AORs'}
            onClick={handleToggleAll}
          >
            <SharedIcon
              iconName={isAllVisible ? 'visibility' : 'visibility_off'}
              size="sm"
            />
          </StylishNewButton>
        </div>
        {focusedDistrictId && activeDistrict ? (
          <div className="mt-2">
            <ActiveFilterChips chips={activeChips} />
          </div>
        ) : (
          <div className="mt-2 d-flex flex-wrap gap-2">
            <StylishNewButton
              className={
                selectedAreaId === null
                  ? 'button--primary'
                  : 'button--secondary'
              }
              onClick={() => setSelectedAreaId(null)}
            >
              All
            </StylishNewButton>
            {areas.map((area) => (
              <StylishNewButton
                key={area.id}
                className={
                  selectedAreaId === area.id
                    ? 'button--primary'
                    : 'button--secondary'
                }
                onClick={() => setSelectedAreaId(area.id)}
              >
                {area.name}
              </StylishNewButton>
            ))}
          </div>
        )}
      </div>
      <div className="flex-grow-1 overflow-auto" style={{ maxHeight: '60vh' }}>
        {isLoading ? (
          <div className="text-center text-muted py-4">Loading AORs...</div>
        ) : selectedSectorId ? (
          <>
            {(subUnitsBySectorId[selectedSectorId] ?? []).map((subUnit) => (
              <HierarchyItemCard
                key={subUnit.id}
                allAorIds={allAorIds}
                item={subUnit}
                sectionKey={`subunit-${subUnit.id}`}
                marginClass="mb-2"
              />
            ))}
          </>
        ) : (
          filteredDistricts.map((district) => (
            <HierarchyItemCard
              key={district.id}
              allAorIds={allAorIds}
              descendantIds={[
                ...(sectorsByDistrictId[district.id] ?? []).map((s) => s.id),
                ...(sectorsByDistrictId[district.id] ?? []).flatMap((s) =>
                  (subUnitsBySectorId[s.id] ?? []).map((su) => su.id)
                ),
              ]}
              item={district}
              sectionKey={`district-${district.id}`}
              marginClass="mb-2"
            >
              <div>
                {(sectorsByDistrictId[district.id] ?? []).map((sector) => {
                  const hasSubUnits =
                    (subUnitsBySectorId[sector.id]?.length ?? 0) > 0;
                  return (
                    <HierarchyItemCard
                      key={sector.id}
                      allAorIds={allAorIds}
                      level={SECTOR}
                      descendantIds={(subUnitsBySectorId[sector.id] ?? []).map(
                        (su) => su.id
                      )}
                      item={sector}
                      sectionKey={`sector-${sector.id}`}
                      onAction={
                        hasSubUnits
                          ? () => {
                              setFocusedDistrictId(district.id);
                              setSelectedSectorId(sector.id);
                            }
                          : undefined
                      }
                      actionLabel={
                        hasSubUnits ? 'Show Sector SubUnits' : undefined
                      }
                      noActionMessage={!hasSubUnits ? 'No SubUnits' : undefined}
                      marginClass="mb-2"
                    />
                  );
                })}
              </div>
            </HierarchyItemCard>
          ))
        )}
      </div>
    </div>
  );
};

export default AORs;
